import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { useConfirm } from "@/context/ConfirmContext";
import { AHeader, ACard, inputDark } from "@/components/shared/AdminKit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useTr } from "@/lib/tr";
import { catalogError } from "@/lib/catalogError";
import { Loader2, Save, Trash2, GripVertical, Eye, EyeOff } from "lucide-react";

export default function AdminCategories() {
  const { t } = useContent();
  const tr = useTr();
  const confirm = useConfirm();
  const [platforms, setPlatforms] = useState([]);
  const [platformId, setPlatformId] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const dragId = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    const q = platformId !== "all" ? `?platform_id=${platformId}` : "";
    return api.get(`/admin/categories${q}`).then((r) => {
      setRows(r.data.map((c) => ({ ...c, active: c.active !== false })));
      setLoading(false);
    });
  }, [platformId]);
  useEffect(() => { api.get("/admin/platforms").then((r) => setPlatforms(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (id, k, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v } : r)));
  const platName = (id) => platforms.find((p) => p.id === id)?.name || "—";

  const save = async (row) => {
    setSavingId(row.id);
    try {
      await api.put(`/admin/categories/${row.id}`, { name: row.name, name_en: row.name_en, platform_id: row.platform_id, active: row.active });
      toast.success(t("cat.saved", "Catégorie enregistrée")); load();
    } catch (e) { toast.error(catalogError(e, tr, t("cat.save_error", "Échec de l'enregistrement"))); }
    finally { setSavingId(null); }
  };

  const toggleActive = async (row) => {
    const v = !row.active; setField(row.id, "active", v);
    try { await api.put(`/admin/categories/${row.id}`, { active: v }); } catch { toast.error(t("cat.save_error", "Échec")); }
  };

  const del = async (row) => {
    if (!(await confirm({ title: t("cat.confirm_del_title", "Supprimer la catégorie"), description: t("cat.confirm_del", "Supprimer cette catégorie ?"), danger: true }))) return;
    await api.delete(`/admin/categories/${row.id}`); toast.success(t("cat.deleted", "Catégorie supprimée")); load();
  };

  const onDrop = async (targetId) => {
    const from = rows.findIndex((r) => r.id === dragId.current);
    const to = rows.findIndex((r) => r.id === targetId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRows(next);
    dragId.current = null;
    try { await api.post("/admin/categories/reorder", { ids: next.map((r) => r.id) }); }
    catch { toast.error(t("cat.reorder_error", "Échec du réordonnancement")); }
  };

  return (
    <div>
      <AHeader title={t("cat.title", "Catégories")} subtitle={t("cat.subtitle", "Éditez FR/EN, changez de plateforme, réordonnez par glisser-déposer, activez/désactivez.")} />
      <ACard className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={platformId} onValueChange={setPlatformId}>
            <SelectTrigger data-testid="cat-platform-filter" className="w-56 border-slate-700 bg-slate-800 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("cat.all_platforms", "Toutes les plateformes")}</SelectItem>
              {platforms.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-400" data-testid="cat-count">{rows.length}</span>
        </div>
      </ACard>

      {loading ? <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div> : (
        <ACard>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr><th className="px-2 py-2.5"></th><th className="px-3 py-2.5">{t("cat.col.fr", "Nom (FR)")}</th><th className="px-3 py-2.5">{t("cat.col.en", "Nom (EN)")}</th><th className="px-3 py-2.5">{t("cat.col.platform", "Plateforme")}</th><th className="px-3 py-2.5">{t("cat.col.active", "Actif")}</th><th className="px-3 py-2.5 text-right">{t("cat.col.actions", "Actions")}</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} draggable onDragStart={() => (dragId.current = r.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(r.id)}
                    className="border-b border-slate-800/60 last:border-0" data-testid={`cat-row-${r.id}`}>
                    <td className="px-2 py-2.5 cursor-grab text-slate-500"><GripVertical className="h-4 w-4" /></td>
                    <td className="px-3 py-2.5"><input value={r.name || ""} onChange={(e) => setField(r.id, "name", e.target.value)} data-testid={`cat-fr-${r.id}`} className={inputDark} /></td>
                    <td className="px-3 py-2.5"><input value={r.name_en || ""} onChange={(e) => setField(r.id, "name_en", e.target.value)} data-testid={`cat-en-${r.id}`} placeholder={t("cat.en_ph", "English name")} className={inputDark} /></td>
                    <td className="px-3 py-2.5">
                      <Select value={r.platform_id} onValueChange={(v) => setField(r.id, "platform_id", v)}>
                        <SelectTrigger data-testid={`cat-platform-${r.id}`} className="w-40 border-slate-700 bg-slate-800 text-white"><SelectValue>{platName(r.platform_id)}</SelectValue></SelectTrigger>
                        <SelectContent>{platforms.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2.5">
                      <button onClick={() => toggleActive(r)} data-testid={`cat-active-${r.id}`} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium ${r.active ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-700 text-slate-400"}`}>{r.active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {r.active ? t("cat.on", "Actif") : t("cat.off", "Inactif")}</button>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => save(r)} disabled={savingId === r.id} data-testid={`cat-save-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">{savingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {t("cat.save", "Enregistrer")}</button>
                        <button onClick={() => del(r)} data-testid={`cat-del-${r.id}`} className="text-red-400"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-500">{t("cat.empty", "Aucune catégorie.")}</td></tr>}
              </tbody>
            </table>
          </div>
        </ACard>
      )}
    </div>
  );
}
